import { useEffect, useState, useCallback } from "react";
import { Slide } from "@mui/material";
import Styles from "./styles";
import { VeryBtnTransparent } from "../../common/v2/VeryBtnTransparent";
import { classVeryBtnTransparent } from "../../common/v2/VeryBtnTransparent/classVeryBtnTransparent";
import { classVeryIconGoogle } from "../../common/v2/VeryIconGoogle/classVeryIconGoogle";
import { getDispatchAudit } from "../../../services/trip";
import { useSessionContext } from "../../../context/session.context";
import { getFormattedNumber } from "../../../utils/handler_validations";

/**
 * Panel lateral que muestra la auditoría de despacho de un viaje:
 * Qué conductores estaban cercanos al momento de la solicitud,
 * su distancia, si tenían WebSocket activo y si recibieron Push.
 * Permite al equipo operativo diagnosticar rechazos de viajes.
 */
export const DispatchAuditPanel = ({ tripId = 0, onClose }) => {
  const session = useSessionContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // { trip, driversFound, noDriversNearby, audit }

  const isOpen = tripId > 0;

  const fetchAudit = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
    setData(null);
    const resp = await getDispatchAudit(tripId, session.value.jwt);
    setLoading(false);
    if (resp.error) {
      setError(resp.error.message || "No fue posible cargar el registro");
      return;
    }
    setData(resp.body);
  }, [tripId, session.value.jwt]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const renderDriverRow = (driver, index) => {
    const hasWs = driver.notified;
    const hasToken = driver.hasToken;
    let rowClass = "div-driverRow";
    if (hasWs) rowClass += " notified";
    else if (!hasToken) rowClass += " no-token";
    else rowClass += " not-notified";

    return (
      <div key={driver.driverId || index} className={rowClass}>
        <div className="row-name">
          <div>
            <div className="txt-name">{driver.driverName}</div>
            <div className="txt-tel">{driver.driverTel || "Sin teléfono"}</div>
          </div>
        </div>

        <div className="row-stats">
          <span className="stat">
            📍 {getFormattedNumber(driver.distanceKm, true, 2)} km
          </span>
          <span className="stat">
            ⏱ {getFormattedNumber(driver.durationMin, true, 0)} min
          </span>
        </div>

        <div className="row-badges">
          <span className={`badge ${hasWs ? "badge-ws" : "badge-no-ws"}`}>
            {hasWs ? "✓ WS activo" : "✗ Sin WebSocket"}
          </span>
          <span className={`badge ${hasToken ? "badge-push" : "badge-no-push"}`}>
            {hasToken ? "✓ Push token" : "✗ Sin Push token"}
          </span>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="div-loading">
          <span className="txt-loading">Cargando registro...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="div-error">
          <span className="txt-error">{error}</span>
        </div>
      );
    }

    if (!data) return null;

    const noAudit = !data.audit || data.audit.length === 0;

    // Calcular contenido del banner de diagnóstico dinámicamente según el estatus del viaje
    let alertClass = "div-alertHasDrivers";
    let alertIcon = "⚠️";
    let alertTitle = `${data.driversFound} conductor(es) encontrado(s) pero el viaje se rechazó o expiró`;
    let alertSub = "Revisa si tienen WebSocket y Push token activos. Si no tienen Push token, no reciben notificaciones.";

    const estatus = data.trip?.estatus;
    if (estatus >= 2 && estatus <= 4) {
      alertClass += " success";
      alertIcon = "🟢";
      alertTitle = `${data.driversFound} conductor(es) notificado(s) | Viaje en curso`;
      alertSub = "El viaje fue aceptado exitosamente por un conductor y se encuentra en transcurso.";
    } else if (estatus === 5) {
      alertClass += " success";
      alertIcon = "✓";
      alertTitle = `${data.driversFound} conductor(es) notificado(s) | Viaje completado`;
      alertSub = "El viaje finalizó con éxito.";
    } else if (estatus === 1) {
      alertClass += " info";
      alertIcon = "⏳";
      alertTitle = `${data.driversFound} conductor(es) notificado(s) | Buscando conductor...`;
      alertSub = "El viaje sigue activo buscando que un conductor acepte la solicitud.";
    }

    return (
      <div className="div-body">
        {/* Resumen del viaje */}
        <div>
          <span className="txt-sectionTitle">Viaje #{tripId}</span>
          <div className="div-tripCard">
            <div className="row">
              <span className="lbl-key">Pasajero</span>
              <span className="lbl-val">
                {data.trip?.passengerName} {data.trip?.passengerLastName}
              </span>
            </div>
            <div className="row">
              <span className="lbl-key">Costo</span>
              <span className="lbl-val">
                ${getFormattedNumber(data.trip?.cost, true, 2)} MXN
              </span>
            </div>
            <div className="row">
              <span className="lbl-key">Distancia</span>
              <span className="lbl-val">
                {getFormattedNumber(data.trip?.distance, true, 2)} km
              </span>
            </div>
          </div>
        </div>

        {/* Alerta de diagnóstico */}
        {data.noDriversNearby ? (
          <div className="div-alertNoDrivers">
            <span className="icon-alert">🔴</span>
            <div>
              <p className="txt-alert">
                No se encontraron conductores cercanos
              </p>
              <span className="txt-sub">
                Al momento de la solicitud, ningún conductor activo estaba
                disponible dentro del radio de búsqueda. No es un problema
                de notificaciones ni de la app.
              </span>
            </div>
          </div>
        ) : !noAudit ? (
          <div className={alertClass}>
            <span className="icon-alert">{alertIcon}</span>
            <div>
              <p className="txt-alert">{alertTitle}</p>
              <span className="txt-sub">{alertSub}</span>
            </div>
          </div>
        ) : null}

        {/* Lista de conductores cercanos */}
        {!noAudit && (
          <div>
            <span className="txt-sectionTitle">
              Conductores cercanos al solicitar ({data.driversFound})
            </span>
            <div className="div-driversList">
              {data.audit.map((d, i) => renderDriverRow(d, i))}
            </div>
          </div>
        )}

        {noAudit && !data.noDriversNearby && (
          <div className="div-noAudit">
            <span>Sin datos de asignación para este viaje.</span>
            <br />
            <span style={{ fontSize: 11, color: "#d1d5db" }}>
              La bitácora solo registra viajes solicitados después de que esta
              función fue activada.
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Slide direction="left" in={isOpen} timeout={350} unmountOnExit>
      <Styles.DivMain>
        <div className="div-header">
          <label className="txt-title">Log de Asignación</label>
          <VeryBtnTransparent
            CSS={[]}
            colorTheme={classVeryBtnTransparent.COLOR_THEME.BLACK_S2}
            icon={{
              name: "close",
              type: classVeryIconGoogle.ICON_TYPE.SYMBOLS_OUTLINED,
              size: 22,
            }}
            onClick={onClose}
          />
        </div>
        {renderContent()}
      </Styles.DivMain>
    </Slide>
  );
};
